import React, { useState } from 'react';
import { ETFSimilarityETF, ETFHoldingEntry } from '../../services/portfolio';

interface Props {
  etfs: ETFSimilarityETF[];
  matrix: number[][];
  holdingsByEtf: Record<string, ETFHoldingEntry[]>;
}

function interpolateColor(value: number): { background: string; color: string } {
  const r = Math.round(255 * (1 - value));
  const g = Math.round(255 - 250 * value);
  const b = Math.round(255 - 200 * value);
  return { background: `rgb(${r},${g},${b})`, color: value > 0.5 ? '#ffffff' : '#18181b' };
}

const TOP_N = 10;

interface BreakdownProps {
  etfA: ETFSimilarityETF;
  etfB: ETFSimilarityETF;
  overlap: number;
  holdingsA: ETFHoldingEntry[];
  holdingsB: ETFHoldingEntry[];
}

const BreakdownPanel: React.FC<BreakdownProps> = ({ etfA, etfB, overlap, holdingsA, holdingsB }) => {
  const mapA = new Map(holdingsA.map(h => [h.identifier, h]));
  const mapB = new Map(holdingsB.map(h => [h.identifier, h]));

  const shared = holdingsA
    .filter(h => mapB.has(h.identifier))
    .map(h => ({ ...h, weightB: mapB.get(h.identifier)!.weight }))
    .sort((a, b) => Math.min(b.weight, b.weightB) - Math.min(a.weight, a.weightB));

  const uniqueA = holdingsA.filter(h => !mapB.has(h.identifier));
  const uniqueB = holdingsB.filter(h => !mapA.has(h.identifier));

  const fmt = (w: number) => `${(w * 100).toFixed(2)}%`;

  const SectionLabel: React.FC<{ label: string; count: number }> = ({ label, count }) => (
    <div style={{
      fontSize: '0.68rem', fontWeight: 700, color: '#71717a',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      display: 'flex', justifyContent: 'space-between', marginBottom: '5px',
    }}>
      <span>{label}</span>
      <span style={{ fontWeight: 400 }}>{count}</span>
    </div>
  );

  const SharedRow: React.FC<{ h: ETFHoldingEntry & { weightB: number } }> = ({ h }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0', borderBottom: '1px solid #f9f9f9', gap: '6px' }}>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '0.77rem', fontWeight: 600, color: '#18181b' }}>{h.identifier}</span>
        {h.name && h.name !== h.identifier && (
          <span style={{ fontSize: '0.7rem', color: '#a1a1aa', marginLeft: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {h.name.length > 22 ? h.name.slice(0, 22) + '…' : h.name}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
        <span style={{ fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', color: '#000537', fontWeight: 600 }}>{fmt(h.weight)}</span>
        <span style={{ fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', color: '#7c3aed', fontWeight: 600 }}>{fmt(h.weightB)}</span>
      </div>
    </div>
  );

  const UniqueRow: React.FC<{ h: ETFHoldingEntry; color: string }> = ({ h, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0', borderBottom: '1px solid #f9f9f9', gap: '6px' }}>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '0.77rem', fontWeight: 600, color: '#18181b' }}>{h.identifier}</span>
        {h.name && h.name !== h.identifier && (
          <span style={{ fontSize: '0.7rem', color: '#a1a1aa', marginLeft: '5px' }}>
            {h.name.length > 22 ? h.name.slice(0, 22) + '…' : h.name}
          </span>
        )}
      </div>
      <span style={{ fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums', color, fontWeight: 600, flexShrink: 0 }}>{fmt(h.weight)}</span>
    </div>
  );

  const hasData = holdingsA.length > 0 || holdingsB.length > 0;

  return (
    <div style={{ fontSize: '0.82rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #f4f4f5' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#18181b' }}>
          <span style={{ color: '#000537' }}>{etfA.ticker}</span>
          <span style={{ color: '#a1a1aa', margin: '0 5px' }}>↔</span>
          <span style={{ color: '#7c3aed' }}>{etfB.ticker}</span>
        </div>
        <div style={{ fontSize: '0.73rem', color: '#71717a', marginTop: '2px' }}>
          {(overlap * 100).toFixed(1)}% portfolio overlap coefficient
        </div>
      </div>

      {!hasData ? (
        <div style={{ color: '#a1a1aa', fontSize: '0.8rem', textAlign: 'center', paddingTop: '16px' }}>
          No holdings data available
        </div>
      ) : (
        <div style={{ maxHeight: 380, overflowY: 'auto' }}>
          {/* Shared */}
          {shared.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <SectionLabel label="Shared holdings" count={shared.length} />
              <div style={{ fontSize: '0.67rem', color: '#a1a1aa', display: 'flex', justifyContent: 'flex-end', gap: '5px', marginBottom: '3px' }}>
                <span style={{ color: '#000537', fontWeight: 600 }}>{etfA.ticker}</span>
                <span style={{ color: '#7c3aed', fontWeight: 600 }}>{etfB.ticker}</span>
              </div>
              {shared.slice(0, TOP_N).map(h => <SharedRow key={h.identifier} h={h} />)}
              {shared.length > TOP_N && (
                <div style={{ fontSize: '0.7rem', color: '#a1a1aa', paddingTop: '4px' }}>+{shared.length - TOP_N} more</div>
              )}
            </div>
          )}

          {/* Only A */}
          {uniqueA.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <SectionLabel label={`Only in ${etfA.ticker}`} count={uniqueA.length} />
              {uniqueA.slice(0, TOP_N).map(h => <UniqueRow key={h.identifier} h={h} color="#000537" />)}
              {uniqueA.length > TOP_N && (
                <div style={{ fontSize: '0.7rem', color: '#a1a1aa', paddingTop: '4px' }}>+{uniqueA.length - TOP_N} more</div>
              )}
            </div>
          )}

          {/* Only B */}
          {uniqueB.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <SectionLabel label={`Only in ${etfB.ticker}`} count={uniqueB.length} />
              {uniqueB.slice(0, TOP_N).map(h => <UniqueRow key={h.identifier} h={h} color="#7c3aed" />)}
              {uniqueB.length > TOP_N && (
                <div style={{ fontSize: '0.7rem', color: '#a1a1aa', paddingTop: '4px' }}>+{uniqueB.length - TOP_N} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ETFSimilarityHeatmap: React.FC<Props> = ({ etfs, matrix, holdingsByEtf }) => {
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(null);

  if (etfs.length < 2 || !matrix.length) return null;

  const n = etfs.length;
  const cellSize = n <= 4 ? 72 : n <= 6 ? 60 : 50;
  const labelWidth = n <= 4 ? 72 : n <= 6 ? 60 : 50;

  const handleCellClick = (i: number, j: number) => {
    if (i === j) return;
    if (selectedCell && selectedCell[0] === i && selectedCell[1] === j) {
      setSelectedCell(null);
    } else {
      setSelectedCell([i, j]);
    }
  };

  const isSelected = (i: number, j: number) =>
    selectedCell !== null &&
    ((selectedCell[0] === i && selectedCell[1] === j) ||
     (selectedCell[0] === j && selectedCell[1] === i));

  const breakdown = selectedCell
    ? {
        etfA: etfs[selectedCell[0]],
        etfB: etfs[selectedCell[1]],
        overlap: matrix[selectedCell[0]][selectedCell[1]],
        holdingsA: holdingsByEtf[String(etfs[selectedCell[0]].id)] || [],
        holdingsB: holdingsByEtf[String(etfs[selectedCell[1]].id)] || [],
      }
    : null;

  return (
    <div className="card card-forest mb-4">
      <div className="card-body p-3 p-md-4">
        <div className="mb-3">
          <h5 className="card-title mb-0 fw-semibold text-forest-600" style={{ fontSize: '0.95rem' }}>
            ETF Similarity
          </h5>
          <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '2px' }}>
            Portfolio overlap coefficient — click any cell to see shared and unique holdings
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Heatmap */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: '3px' }}>
                <thead>
                  <tr>
                    <th style={{ width: labelWidth, minWidth: labelWidth }} />
                    {etfs.map(etf => (
                      <th
                        key={etf.id}
                        title={etf.name}
                        style={{
                          width: cellSize, minWidth: cellSize,
                          fontSize: '0.75rem', fontWeight: 600, color: '#52525b',
                          textAlign: 'center', paddingBottom: '6px', verticalAlign: 'bottom',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {etf.ticker}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {etfs.map((rowEtf, i) => (
                    <tr key={rowEtf.id}>
                      <td
                        title={rowEtf.name}
                        style={{
                          fontSize: '0.75rem', fontWeight: 600, color: '#52525b',
                          textAlign: 'right', paddingRight: '10px',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          maxWidth: labelWidth,
                        }}
                      >
                        {rowEtf.ticker}
                      </td>
                      {etfs.map((colEtf, j) => {
                        const val = matrix[i]?.[j] ?? 0;
                        const isDiag = i === j;
                        const sel = isSelected(i, j);
                        const cellStyle = isDiag
                          ? { background: '#f4f4f5', color: '#a1a1aa' }
                          : interpolateColor(val);

                        return (
                          <td
                            key={colEtf.id}
                            title={isDiag ? rowEtf.name : `${rowEtf.name} ↔ ${colEtf.name}: ${(val * 100).toFixed(1)}% overlap`}
                            onClick={() => handleCellClick(i, j)}
                            style={{
                              width: cellSize, height: cellSize,
                              borderRadius: '6px',
                              textAlign: 'center', verticalAlign: 'middle',
                              fontSize: '0.8rem', fontWeight: isDiag ? 400 : 600,
                              fontVariantNumeric: 'tabular-nums',
                              cursor: isDiag ? 'default' : 'pointer',
                              outline: sel ? '2px solid #52525b' : 'none',
                              outlineOffset: '2px',
                              transition: 'opacity 0.12s ease',
                              ...cellStyle,
                            }}
                            onMouseEnter={e => { if (!isDiag) (e.currentTarget as HTMLElement).style.opacity = '0.75'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                          >
                            {isDiag ? '—' : `${(val * 100).toFixed(0)}%`}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>

          {/* Breakdown panel */}
          <div style={{ flex: '1 1 260px', minWidth: 240, display: 'flex', flexDirection: 'column' }}>
            {breakdown ? (
              <BreakdownPanel {...breakdown} />
            ) : (
              <div style={{ flex: 1, border: '1px dashed #e4e4e7', borderRadius: '10px', background: '#fdfdfd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '24px' }}>
                <svg width="44" height="30" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="15" stroke="#d4d4d8" strokeWidth="1.5" fill="white" />
                  <circle cx="32" cy="16" r="15" stroke="#d4d4d8" strokeWidth="1.5" fill="white" />
                  <path d="M24 3.8 C28.5 7 31 11.2 31 16 C31 20.8 28.5 25 24 28.2 C19.5 25 17 20.8 17 16 C17 11.2 19.5 7 24 3.8Z" fill="#e4e4e7" />
                </svg>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#52525b', marginBottom: '4px' }}>
                    Select a pair to compare
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', lineHeight: 1.5 }}>
                    Click any cell to see shared holdings<br />and what's unique to each ETF
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ETFSimilarityHeatmap;
